import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * Secure Integration Page
 * Showcases FHIR capabilities and internal integration features
 */
const SecureIntegrationPage = () => {
  const fhirResources = [
    { name: "Patient", description: "Demographics and administrative information about patients" },
    { name: "Encounter", description: "Patient visits, admissions, and healthcare interactions" },
    { name: "Observation", description: "Lab results, vital signs, and clinical measurements" },
    { name: "Condition", description: "Problems, diagnoses, and health concerns" },
    { name: "Procedure", description: "Actions performed on or for a patient" },
    { name: "MedicationRequest", description: "Prescription and medication orders" },
    { name: "DiagnosticReport", description: "Lab reports, imaging studies, and clinical reports" },
    { name: "Claim", description: "Healthcare billing and insurance claims" },
    { name: "ExplanationOfBenefit", description: "Adjudication details and payment information" },
    { name: "Coverage", description: "Insurance coverage and eligibility information" }
  ];

  const internalIntegrations = [
    {
      title: "Electronic Health Records (EHR)",
      systems: ["Epic", "Cerner", "MEDITECH", "Allscripts", "athenahealth"],
      description: "Seamless bidirectional integration with major EHR systems for real-time clinical data exchange."
    },
    {
      title: "Practice Management Systems",
      systems: ["NextGen", "eClinicalWorks", "Greenway", "Kareo"],
      description: "Connect scheduling, billing, and patient management data for comprehensive operational insights."
    },
    {
      title: "Revenue Cycle Management",
      systems: ["Waystar", "Availity", "Change Healthcare", "Optum"],
      description: "Integrate claims processing, eligibility verification, and payment posting workflows."
    },
    {
      title: "Health Information Exchanges (HIE)",
      systems: ["CommonWell", "Carequality", "eHealth Exchange"],
      description: "Access patient data across organizations through established health information networks."
    }
  ];

  const securityFeatures = [
    {
      title: "HIPAA Compliance",
      description: "Full compliance with HIPAA Privacy and Security Rules, including encryption, access controls, and audit logging.",
      icon: "🔒"
    },
    {
      title: "SOC 2 Type II Certified",
      description: "Independent verification of our security controls, availability, processing integrity, and confidentiality.",
      icon: "✓"
    },
    {
      title: "End-to-End Encryption",
      description: "AES-256 encryption for data at rest and TLS 1.3 for data in transit across all connections.",
      icon: "🔐"
    },
    {
      title: "Role-Based Access Control",
      description: "Granular permissions ensure users only access data necessary for their role.",
      icon: "👥"
    },
    {
      title: "Audit Trail",
      description: "Comprehensive logging of all data access and modifications for compliance and security monitoring.",
      icon: "📋"
    },
    {
      title: "Multi-Factor Authentication",
      description: "Secure authentication with MFA support for all user accounts and API access.",
      icon: "📱"
    }
  ];

  const fhirVersions = [
    { version: "R4", status: "Full Support", description: "Current production standard, recommended for new implementations" },
    { version: "STU3", status: "Supported", description: "Legacy support for existing implementations" },
    { version: "R5", status: "Preview", description: "Early access to upcoming FHIR standard features" }
  ];

  const apiCapabilities = [
    {
      title: "RESTful API",
      description: "Standard REST endpoints for all FHIR resources with full CRUD operations",
      features: ["JSON/XML support", "Pagination", "Search parameters", "Versioning"]
    },
    {
      title: "Bulk Data Export",
      description: "FHIR Bulk Data Access specification for large-scale data extraction",
      features: ["Async processing", "NDJSON format", "Group-level exports", "Delta updates"]
    },
    {
      title: "Subscriptions",
      description: "Real-time notifications for data changes using FHIR Subscriptions",
      features: ["REST hooks", "WebSocket", "Email notifications", "Custom filters"]
    },
    {
      title: "SMART on FHIR",
      description: "OAuth 2.0 based authorization for secure third-party app integration",
      features: ["App launch", "Standalone launch", "Scopes", "Refresh tokens"]
    }
  ];

  return (
    <div className="secure-integration-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Secure <span style={{ color: '#FFC000' }}>Integration</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              Connect your healthcare ecosystem with FHIR-compliant, secure integrations
            </p>
            <p className="text-lg mb-8 text-gray-600">
              Videxa enables seamless data exchange across your organization while maintaining the highest security standards.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cdi-trial"
                style={{
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:bg-[#0E3056] hover:text-white transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FHIR Overview */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              FHIR-Native Architecture
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Built from the ground up on HL7 FHIR standards for true healthcare interoperability
            </p>
          </div>

          {/* FHIR Versions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            {fhirVersions.map((item, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md text-center"
              >
                <div className="text-2xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">
                  FHIR {item.version}
                </div>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${
                  item.status === 'Full Support' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  item.status === 'Supported' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {item.status}
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported FHIR Resources */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Supported FHIR Resources
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Comprehensive support for clinical, financial, and administrative FHIR resources
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {fhirResources.map((resource, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg hover:shadow-md transition-shadow"
              >
                <h3 className="font-semibold text-[#0E3056] dark:text-[#FFC000] mb-2">
                  {resource.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {resource.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Capabilities */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            API Capabilities
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Powerful APIs designed for healthcare integration needs
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {apiCapabilities.map((api, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {api.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {api.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {api.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Integrations */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Pre-Built Integrations
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Connect with leading healthcare systems out of the box
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {internalIntegrations.map((integration, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {integration.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {integration.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {integration.systems.map((system, idx) => (
                    <span
                      key={idx}
                      className="bg-[#0E3056] text-white px-3 py-1 rounded text-sm"
                    >
                      {system}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-center">
            Enterprise-Grade Security
          </h2>
          <p className="text-xl text-center text-gray-200 mb-12 max-w-3xl mx-auto">
            Your data security is our top priority. We implement comprehensive safeguards at every layer.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white/10 p-6 rounded-lg"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-200">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Process */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Simple Integration Process
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              {[
                { step: "1", title: "Discovery", description: "We assess your systems and integration requirements" },
                { step: "2", title: "Configuration", description: "Set up secure connections with your healthcare systems" },
                { step: "3", title: "Testing", description: "Validate data flows in a sandbox environment" },
                { step: "4", title: "Go Live", description: "Deploy to production with ongoing support" }
              ].map((item, index) => (
                <div key={index} className="flex-1 text-center">
                  <div className="bg-[#0E3056] dark:bg-[#FFC000] text-white dark:text-gray-900 w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <BlueCallToAction
        heading="Ready to connect your healthcare systems?"
        description="Get started with secure, FHIR-compliant integrations that scale with your organization."
        primaryButtonText="Start Free Trial"
        primaryButtonLink="/cdi-trial"
        secondaryButtonText="Contact Sales"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default SecureIntegrationPage;
